import type { CameraDistanceId, CameraPlatformId } from './presets.js';
import type { MapStyleId } from './map-style.js';
import type { DepthOfFieldSettings } from './depth-of-field.js';
import type { CameraKeyframes } from './camera-keyframes.js';
import { CAMERA_TRACKS } from './camera-keyframes.js';

export const MAX_CINEMATIC_WAYPOINTS = 32;
export interface CinematicOverrides {
  cameraGimbal?: import('./camera-gimbal.js').CameraGimbalId | undefined;
  cameraStyle?: CameraPlatformId | undefined;
  mapStyle?: MapStyleId | undefined;
  cameraAngle?: number | null | undefined;
  cameraDistance?: CameraDistanceId | undefined;
  /** Explicit map scale at the 50mm framing anchor; absent on automatic framing. */
  cameraZoom?: number | null | undefined;
  depthOfField?: DepthOfFieldSettings | undefined;
  presentationAppearance?: import('./presentation-appearance.js').PresentationAppearance | undefined;
}
export interface CinematicWaypoint {
  id: string;
  /** Authored film position; absent on legacy keys timed from their recorded sample. */
  filmProgress?: number | undefined;
  anchor: { pointIndex: number; recordedAt: string };
  overrides: CinematicOverrides;
  transition: { type: 'cut'; timing?: 'arrival' | undefined } | { type: 'smooth'; durationSeconds: number; entireInterval?: boolean | undefined; easing?: 'linear' | 'smooth' | undefined; timing?: 'arrival' | undefined };
}
export interface CinematicSequence { version: 1; waypoints: CinematicWaypoint[]; cameraTracks?: CameraKeyframes | undefined }
export const emptyCinematicSequence = (): CinematicSequence => ({ version: 1, waypoints: [] });

export const CUT_SCENE_PROPERTIES: readonly (keyof CinematicOverrides)[] = ['cameraStyle', 'mapStyle'];
export function defaultSceneKeyTransition(property?: keyof CinematicOverrides): CinematicWaypoint['transition'] {
  return property && CUT_SCENE_PROPERTIES.includes(property) ? { type: 'cut', timing: 'arrival' }
    : { type: 'smooth', durationSeconds: 2, timing: 'arrival', easing: 'smooth' };
}

export function cinematicKeyCount(sequence?: CinematicSequence): number {
  return (sequence?.waypoints.length ?? 0) + CAMERA_TRACKS.reduce((total, axis) => total + (sequence?.cameraTracks?.[axis]?.length ?? 0), 0);
}

/** Includes enabled waypoint focus when choosing render resources and entitlements. */
export function cinematicUsesDepthOfField(sequence?: CinematicSequence): boolean {
  return sequence?.waypoints.some(item => item.overrides.depthOfField?.enabled) === true;
}
